/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useI18n } from '@/context/I18nContext';
import { formatUnixTimestampMs } from '@/lib/formatting/dateTime';
import type { ParsedLastValueCsv } from '@/lib/timeSeries/parseLastValueCsv';

interface CurrentValuePreviewProps {
    preview: ParsedLastValueCsv;
}

function CurrentValuePreview({ preview }: CurrentValuePreviewProps) {
    const { locale, t } = useI18n();

    return (
        <div className="w-full overflow-x-auto rounded-box border border-base-content/20">
            <table className="table table-sm w-full">
                <thead className="bg-base-content/10 text-sm text-base-content/80">
                    <tr>
                        <th className="px-4 py-3" scope="col">{t('URN')}</th>
                        <th className="px-4 py-3" scope="col">{t('Date')}</th>
                        <th className="px-4 py-3" scope="col">{t('Value')}</th>
                    </tr>
                </thead>

                <tbody className="divide-y divide-base-300">
                    {preview.rows.map((row) => (
                        <tr
                            key={`${row.urn}-${row.timestamp}`}
                            className="even:bg-base-content/5 hover:bg-base-content/10"
                        >
                            <td className="align-top break-all px-4 py-3 font-mono text-xs text-base-content/80 md:text-sm">
                                {row.urn || '-'}
                            </td>
                            <td className="align-top px-4 py-3 font-mono text-xs text-base-content/80 md:text-sm">
                                {formatUnixTimestampMs(row.timestamp, locale, '-')}
                            </td>
                            <td className="align-top px-4 py-3 font-mono text-xs text-base-content/80 md:text-sm">
                                {row.unit ? `${row.value} ${row.unit}` : row.value || '-'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default CurrentValuePreview;
